import { AppState } from "../app/store/rootReducers";
import Validator from "../validators/Validator";
import { CampaignConstants } from "../constants/CampaignConstants";
import MiddlewareEntities from "../constants/MiddlewareEntities";
import config from "../config/config";

export default class CampaignBusinessRuleFacade {

  static isSubmitAllowed(state: AppState): boolean {
    // Terms checkbox must be shacked if it is applicable
    if (state.campaign.overview.p2pDj === 1) {
      return true;
    }

    if (this.isIrregularTermsCheckbox(state)) {
      return false;
    }

    // Email must be fulfilled if it is applicable
    if (this.isIrregularEmailInput(state)) {
      return false;
    }

    if (this.isApplicableDeliveryOptions(state)) {
      // Store must be selected for PIS option
      if (this.isIrregularPIS(state)) {
        return false;
      }

      // Address must be selected for STO option
      if (this.isIrregularSTO(state)) {
        return false;
      }
    }

    // Disable submit during submit process
    if (state.ui?.loading && state.ui?.entity === MiddlewareEntities.CAMPAIGN.SUBMIT) {
      return false;
    }

    // If isApplicableAgentCssForm css acceptance is required for submit
    return !(this.isApplicableAgentCssForm(state) && state.campaign.overview.acceptanceStatus !== CampaignConstants.acceptance.status.GIVEN);
  }

  static isContactEmailValid(state: AppState): boolean {
    return Validator.isValidEmail(state.campaign.overview.contactEmail);
  }

  static isApplicableDeliveryOptions(state: AppState): boolean {
    // Delivery options are not available for p2p campaigns
    if (state.campaign.overview.p2p === 1 || state.campaign.overview.p2pDj === 1) {
      return false;
    }

    // Delivery options are available only for channel01 (channelId - 1)
    return state.campaign.overview.cnlId === 1;
  }

  static isApplicableEanexButton(state: AppState): boolean {
    return (
      this.isApplicableDeliveryOptions(state) &&
      state.campaign.overview.isActiveContactForMsisdn
    );
  }

  static isApplicableDeliveryOptionPIS(state: AppState): boolean {
    // PIS is available only if deliveryOptions are available and if is NOT tpUpSell
    return (
      this.isApplicableDeliveryOptions(state) &&
      state.campaign.overview.upsell !== 1 &&
      state.campaign.overview.tp !== null &&
      !state.campaign.overview.tp.home &&
      !state.campaign.overview.tp.drivey
    );
  }

  static isApplicableESIMCard(state: AppState): boolean {
    return (state.campaign.overview.upsell === 0 &&
      state.campaign.overview.p2p === 0 &&
      state.campaign.overview.cmUser.isAgent === false && config.isEsimCmAllowed);
  }

  static isApplicableDeliveryOptionSTO(state: AppState): boolean {
    // STO is available only if deliveryOptions are available and if PIS is available
    return this.isApplicableDeliveryOptions(state) && this.isApplicableDeliveryOptionPIS(state);
  }

  static isApplicableEmailInput(state: AppState): boolean {
    const { SOFT_ELECTRO, SOFT_EANEX } = CampaignConstants.deliveryOptions;
    const { deliveryOption } = state.campaign.overview;

    // Email input is not necessary for p2p campaigns
    if (state.campaign.overview.p2p === 1 || state.campaign.overview.p2pDj === 1) {
      return false;
    }

    // Email input is not necessary for soft electro campaigns
    return !(
      this.isApplicableDeliveryOptions(state) &&
      [SOFT_ELECTRO, SOFT_EANEX].includes(deliveryOption)
    );
  }

  static isApplicableAgentCssForm(state: AppState): boolean {
    // Agent CSS form for sending and monitoring user CSS response is not applicable for p2p campaigns
    if (state.campaign.overview.p2p === 1) {
      return false;
    }

    if (this.isApplicableDeliveryOptions(state)) {
      return false;
    }

    return state.campaign.overview.cmUser.isAgent;
  }

  static isApplicableTermsCheckbox(state: AppState): boolean {
    // Terms checkbox is applicable only for regular users (...not for agents)
    return !(state.campaign.overview.p2pDj === 1 || state.campaign.overview.cmUser.isAgent);
  }

  static isApplicableConsentSection(state: AppState): boolean {
    // Consent sections are applicable only for customers
    return !state.campaign.overview.cmUser.isAgent;
  }

  static isApplicableSubmitInfoSection(state: AppState): boolean {
    const { SOFT_ELECTRO, SOFT_EANEX } = CampaignConstants.deliveryOptions;
    const { deliveryOption } = state.campaign.overview;

    if (state.campaign.overview.p2pDj === 1) {
      return true;
    }

    if (this.isApplicableDeliveryOptions(state)) {
      return !([SOFT_ELECTRO, SOFT_EANEX].includes(deliveryOption));
    }

    return true;
  }

  static isIrregularTermsCheckbox(state: AppState): boolean {
    return this.isApplicableTermsCheckbox(state) && !state.campaign.overview.userTermsStatus;
  }

  static isIrregularEmailInput(state: AppState): boolean {
    return this.isApplicableEmailInput(state) && !this.isContactEmailValid(state);
  }

  static isIrregularPIS(state: AppState): boolean {
    const { PIS } = CampaignConstants.deliveryOptions;
    const { deliveryOption, selectedStoreId } = state.campaign.overview;

    return deliveryOption === PIS && !selectedStoreId;
  }

  static isIrregularSTO(state: AppState): boolean {
    const { STO } = CampaignConstants.deliveryOptions;
    const { deliveryOption, isDeliveryAddressSelected } = state.campaign.overview;

    return deliveryOption === STO && !isDeliveryAddressSelected;
  }

}
